import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Space, Table, Tag, Typography, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { TelegramAccountSuggestion } from '@lens/shared';
import {
  backfillTelegramAvatars,
  importTelegramAccounts,
  linkTelegramAccounts,
  listTelegramAccounts,
} from './api';

const { Paragraph, Text } = Typography;

/**
 * Экран «Аккаунты Telegram» (администратор экземпляра). Показывает всех,
 * кто писал в подключённых чатах, рядом с подобранными пользователями
 * Lens. Связываем не сами: цена ошибки — чужое имя в авторе записи,
 * поэтому пары подтверждает человек, зато сразу списком.
 */
export function TelegramAccountsCard(): JSX.Element {
  const [rows, setRows] = useState<TelegramAccountSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [avatarsBusy, setAvatarsBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTelegramAccounts();
      setRows(res.accounts);
      setSelected(
        res.accounts
          .filter((r) => !r.account.userId && r.suggestedUserId && !r.ambiguous)
          .map((r) => r.account.telegramUserId),
      );
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось загрузить аккаунты');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const matched = useMemo(
    () => rows.filter((r) => !r.account.userId && r.suggestedUserId && !r.ambiguous),
    [rows],
  );

  const handleLink = async () => {
    const links = matched
      .filter((r) => selected.includes(r.account.telegramUserId))
      .map((r) => ({ telegramUserId: r.account.telegramUserId, userId: r.suggestedUserId! }));
    if (links.length === 0) return;
    setBusy(true);
    try {
      const res = await linkTelegramAccounts(links);
      void message.success(
        res.skipped > 0
          ? `Связано: ${res.linked}. Пропущено ${res.skipped} — у этих пользователей уже есть другой Telegram.`
          : `Связано: ${res.linked}`,
      );
      await reload();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось связать аккаунты');
    } finally {
      setBusy(false);
    }
  };

  const handleAvatars = async () => {
    setAvatarsBusy(true);
    try {
      const res = await backfillTelegramAvatars();
      void message.success(
        res.updated > 0
          ? `Аватарки подтянуты: ${res.updated} из ${res.checked}`
          : `Проверено ${res.checked}, новых аватарок нет — у остальных фото закрыто настройками Telegram`,
      );
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось подтянуть аватарки');
    } finally {
      setAvatarsBusy(false);
    }
  };

  /**
   * Разбор выгрузки переписки из Telegram Desktop: у каждого сообщения
   * есть номер и имя отправителя, из них и собираем список писавших.
   */
  const handleImport = async (file: File) => {
    try {
      const raw = JSON.parse(await file.text()) as {
        messages?: Array<{ from?: string; from_id?: string }>;
      };
      const seen = new Map<string, { id: string; firstName: string | null; lastName: string | null }>();
      for (const msg of raw.messages ?? []) {
        const id = (msg.from_id ?? '').replace(/^user/, '');
        if (!id || !/^\d+$/.test(id)) continue;
        const parts = (msg.from ?? '').trim().split(/\s+/);
        seen.set(id, {
          id,
          firstName: parts[0] ?? null,
          lastName: parts.length > 1 ? parts.slice(1).join(' ') : null,
        });
      }
      if (seen.size === 0) {
        void message.warning('В файле не нашлось отправителей — это точно выгрузка переписки?');
        return;
      }
      const accounts = [...seen.values()];
      // Отправляем частями: выгрузка большого чата легко даёт сотни людей.
      for (let i = 0; i < accounts.length; i += 200) {
        await importTelegramAccounts(accounts.slice(i, i + 200));
      }
      void message.success(`Импортировано аккаунтов: ${accounts.length}`);
      await reload();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось разобрать файл');
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }} data-testid="telegram-accounts-card">
      <Paragraph type="secondary">
        Автором записи из чата становится пользователь Lens, поэтому телеграм-аккаунты нужно связать с
        учётными записями. Список наполняется сам — всеми, кто пишет в подключённых чатах.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        message="Списка участников группы Telegram не выдаёт"
        description="Бот видит только тех, кто уже писал. Чтобы охватить молчунов, попросите их нажать «Связать аккаунт» в закреплённом сообщении бота или загрузите выгрузку переписки из Telegram Desktop."
      />

      <Upload.Dragger
        accept=".json"
        maxCount={1}
        showUploadList={false}
        beforeUpload={(file) => {
          void handleImport(file as unknown as File);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Перетащите сюда result.json из выгрузки переписки</p>
        <p className="ant-upload-hint">Файл разбирается в браузере, в Lens уходят только имена и номера аккаунтов</p>
      </Upload.Dragger>

      <Space wrap>
        <Button type="primary" onClick={handleLink} loading={busy} disabled={selected.length === 0}>
          Связать выбранные ({selected.length})
        </Button>
        <Text type="secondary">Найдено пар: {matched.length}</Text>
        <Button loading={avatarsBusy} onClick={handleAvatars}>
          Подтянуть аватарки
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Для тех, кто связал аккаунт раньше: фото берётся из Telegram, если своей аватарки в Lens нет
        </Text>
      </Space>

      <Table
        size="small"
        loading={loading}
        rowKey={(r) => r.account.telegramUserId}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as string[]),
          getCheckboxProps: (r) => ({
            disabled: Boolean(r.account.userId) || !r.suggestedUserId || r.ambiguous,
          }),
        }}
        columns={[
          {
            title: 'Telegram',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <span>{[r.account.firstName, r.account.lastName].filter(Boolean).join(' ') || '—'}</span>
                {r.account.username && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    @{r.account.username}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Пользователь Lens',
            render: (_, r) => {
              if (r.account.userId) return <Tag color="green">{r.account.userDisplayName}</Tag>;
              if (r.ambiguous) return <Tag color="orange">несколько совпадений — выберите вручную</Tag>;
              if (!r.suggestedUserId) return <Text type="secondary">пары не нашлось</Text>;
              return (
                <Space>
                  <span>{r.suggestedUserDisplayName}</span>
                  <Tag>{r.matchedBy === 'username' ? 'по логину' : 'по имени'}</Tag>
                </Space>
              );
            },
          },
          {
            title: 'Последнее сообщение',
            width: 160,
            render: (_, r) =>
              r.account.lastSeenAt ? new Date(r.account.lastSeenAt).toLocaleDateString('ru-RU') : '—',
          },
        ]}
      />
    </Space>
  );
}
